import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Trophy,
  Medal,
  Crown,
  Star,
  Flame,
  Zap,
  TrendingUp,
  Calendar,
  Clock,
  Loader2,
  User
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// Achievement badge icons
const BADGE_ICONS = {
  'workout_5': Star,
  'workout_10': Flame,
  'workout_25': Flame,
  'workout_50': Zap,
  'workout_100': Crown
};

export const LeaderboardPage = () => {
  const { api, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [activeTab, setActiveTab] = useState('leaderboard');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, rankRes, achievementsRes] = await Promise.all([
        api().get(`/leaderboard?period=${period}`),
        api().get(`/leaderboard/my-rank?period=${period}`),
        api().get('/achievements')
      ]);
      
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setMyRank(rankRes.data);
      setAchievements(achievementsRes.data);
    } catch (err) {
      toast.error('Failed to load leaderboard');
    }
    setLoading(false);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-6 w-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-300" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-amber-600" />;
    return <span className="text-muted-foreground font-bold">#{rank}</span>;
  };

  const getRankBgClass = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/50';
    return 'bg-card border-border';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="leaderboard-page">

      <main className="container mx-auto px-6 py-8">
        {/* Page Title & Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
                <Trophy className="h-8 w-8 text-primary inline mr-3" />
                Rankings & <span className="text-primary">Achievements</span>
              </h2>
              <p className="text-muted-foreground">Compete with the community and earn badges</p>
            </div>
            
            {/* Tab Switcher */}
            <div className="flex gap-2 bg-card border border-border p-1">
              <Button
                variant={activeTab === 'leaderboard' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('leaderboard')}
                className={activeTab === 'leaderboard' ? 'bg-primary text-black' : ''}
                data-testid="leaderboard-tab"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Leaderboard
              </Button>
              <Button
                variant={activeTab === 'achievements' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('achievements')}
                className={activeTab === 'achievements' ? 'bg-primary text-black' : ''}
                data-testid="achievements-tab"
              >
                <Star className="h-4 w-4 mr-2" />
                Achievements
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Period Selector & My Rank */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex gap-2 bg-card border border-border p-1">
                {['weekly', 'monthly', 'all-time'].map((p) => (
                  <Button
                    key={p}
                    variant={period === p ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPeriod(p)}
                    className={period === p ? 'bg-primary text-black' : ''}
                  >
                    {p === 'weekly' ? 'This Week' : p === 'monthly' ? 'This Month' : 'All Time'}
                  </Button>
                ))}
              </div>
              
              {myRank && (
                <div className="bg-primary/10 border border-primary/30 px-4 py-2 flex items-center gap-3">
                  <span className="text-muted-foreground">Your Rank:</span>
                  <span className="font-heading text-2xl text-primary">#{myRank.rank}</span>
                  <span className="text-white/70">({myRank.workout_count} workouts)</span>
                </div>
              )}
            </div>

            {/* Leaderboard List */}
            <div className="space-y-3" data-testid="leaderboard-list">
              {leaderboard.length === 0 ? (
                <div className="bg-card border border-border p-12 text-center">
                  <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">No Data Yet</h3>
                  <p className="text-muted-foreground">Complete workouts to appear on the leaderboard!</p>
                </div>
              ) : (
                leaderboard.map((entry, i) => (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`${getRankBgClass(entry.rank)} border p-4 flex items-center gap-4`}
                    data-testid={`leaderboard-entry-${i}`}
                  >
                    {/* Rank */}
                    <div className="w-12 h-12 flex items-center justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    
                    {/* Avatar & Name - Clickable Link to Profile */}
                    <Link 
                      to={`/user/${entry.user_id}`}
                      className="flex items-center gap-4 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                      data-testid={`leaderboard-user-link-${i}`}
                    >
                      <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {entry.profile_picture ? (
                          <img 
                            src={getImageUrl(entry.profile_picture)} 
                            alt={entry.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      
                      {/* Name & Badge */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-heading text-lg uppercase tracking-wider truncate ${
                            entry.user_id === user?.id ? 'text-primary' : 'text-white'
                          }`}>
                            {entry.name}
                            {entry.user_id === user?.id && ' (You)'}
                          </span>
                          {entry.top_badge && (
                            <Badge className="bg-primary/20 text-primary border-primary/30">
                              {entry.top_badge.icon} {entry.top_badge.name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Link>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <div className="text-2xl font-bold text-white">{entry.workout_count}</div>
                        <div className="text-xs text-muted-foreground uppercase">Workouts</div>
                      </div>
                      <div className="hidden md:block">
                        <div className="text-lg font-medium text-white/70">{entry.total_minutes}</div>
                        <div className="text-xs text-muted-foreground uppercase">Minutes</div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Progress Summary */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-white uppercase tracking-wider">Your Progress</h3>
                  <p className="text-muted-foreground">
                    {achievements.unlocked_count} of {achievements.total_count} achievements unlocked
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{achievements.total_workouts}</div>
                  <div className="text-sm text-muted-foreground">Total Workouts</div>
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-yellow-400 transition-all duration-500"
                  style={{ width: `${(achievements.unlocked_count / achievements.total_count) * 100}%` }}
                />
              </div>
            </div>

            {/* Achievements Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="achievements-grid">
              {(achievements.achievements || []).map((ach, i) => {
                const IconComponent = BADGE_ICONS[ach.id] || Star;
                
                return (
                  <motion.div
                    key={ach.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative overflow-hidden border p-6 ${
                      ach.unlocked 
                        ? 'bg-primary/10 border-primary/50' 
                        : 'bg-card border-border opacity-60'
                    }`}
                    data-testid={`achievement-${ach.id}`}
                  >
                    {ach.unlocked && (
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-primary text-black">Unlocked!</Badge>
                      </div>
                    )}
                    
                    <div className="flex items-start gap-4">
                      <div className={`h-14 w-14 flex items-center justify-center text-3xl ${
                        ach.unlocked ? 'bg-primary/20' : 'bg-white/5'
                      }`}>
                        {ach.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-heading text-lg uppercase tracking-wider ${
                          ach.unlocked ? 'text-primary' : 'text-white/50'
                        }`}>
                          {ach.name}
                        </h4>
                        <p className="text-muted-foreground text-sm">{ach.description}</p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className={ach.unlocked ? 'text-primary' : 'text-white/50'}>
                          {ach.progress}/{ach.requirement}
                        </span>
                      </div>
                      <div className="w-full bg-white/10 h-2 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ${
                            ach.unlocked ? 'bg-primary' : 'bg-white/30'
                          }`}
                          style={{ width: `${ach.progress_percent}%` }}
                        />
                      </div>
                    </div>
                    
                    {ach.unlocked && ach.unlocked_at && (
                      <div className="mt-3 text-xs text-muted-foreground">
                        Unlocked {new Date(ach.unlocked_at).toLocaleDateString()}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};
